import React from 'react';
import { BaseConnectorMapping, type MappingSection } from './BaseConnectorMapping';
import type { BaseMappingProps } from './index';
import type { SettingspageTranslations } from '@/hooks/useSettingspageTranslations';

interface MagentoMappingProps extends BaseMappingProps {
  i18n: SettingspageTranslations;
}

export function MagentoMapping(props: MagentoMappingProps) {
  const { i18n } = props;
  const sections: MappingSection[] = [
    // Automatic mappings
    {
      title: i18n.api.modal.configuration.magento.sections.automaticMappings.title,
      description: i18n.api.modal.configuration.magento.sections.automaticMappings.description,
      isAutomatic: true,
      fields: [],
    },

    // Pricing
    {
      title: i18n.api.modal.configuration.magento.sections.pricing.title,
      description: i18n.api.modal.configuration.magento.sections.pricing.description,
      fields: [
        {
          key: 'price_type_map.base',
          label: i18n.api.modal.configuration.magento.fields.basePrice.label,
          type: 'price_type',
          required: true,
          description: i18n.api.modal.configuration.magento.fields.basePrice.description,
        },
        {
          key: 'price_type_map.special',
          label: i18n.api.modal.configuration.magento.fields.specialPrice.label,
          type: 'price_type',
          allowNone: true,
          description: i18n.api.modal.configuration.magento.fields.specialPrice.description,
        },
      ],
    },

    // Product Attributes
    {
      title: i18n.api.modal.configuration.magento.sections.productAttributes.title,
      description: i18n.api.modal.configuration.magento.sections.productAttributes.description,
      fields: [
        {
          key: 'weight_attribute',
          label: i18n.api.modal.configuration.magento.fields.weight.label,
          type: 'attribute',
          filterType: ['number', 'measurement'],
          allowDefault: true,
          description: i18n.api.modal.configuration.magento.fields.weight.description,
        },
        {
          key: 'manufacturer_attribute',
          label: i18n.api.modal.configuration.magento.fields.manufacturer.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          allowDefault: true,
          description: i18n.api.modal.configuration.magento.fields.manufacturer.description,
        },
        {
          key: 'color_attribute',
          label: i18n.api.modal.configuration.magento.fields.color.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          allowNone: true,
          description: i18n.api.modal.configuration.magento.fields.color.description,
        },
        {
          key: 'size_attribute',
          label: i18n.api.modal.configuration.magento.fields.size.label,
          type: 'attribute',
          filterType: ['text', 'select'],
          allowNone: true,
          description: i18n.api.modal.configuration.magento.fields.size.description,
        },
      ],
    },

    // Inventory
    {
      title: i18n.api.modal.configuration.magento.sections.inventory.title,
      description: i18n.api.modal.configuration.magento.sections.inventory.description,
      fields: [
        {
          key: 'inventory.quantity_attribute',
          label: i18n.api.modal.configuration.magento.fields.stockQuantity.label,
          type: 'attribute',
          filterType: ['number'],
          allowDefault: true,
          description: i18n.api.modal.configuration.magento.fields.stockQuantity.description,
        },
        {
          key: 'inventory.manage_stock_attribute',
          label: i18n.api.modal.configuration.magento.fields.manageStock.label,
          type: 'boolean',
          allowDefault: true,
          description: i18n.api.modal.configuration.magento.fields.manageStock.description,
        },
        {
          key: 'inventory.backorders_attribute',
          label: i18n.api.modal.configuration.magento.fields.backorders.label,
          type: 'attribute',
          filterType: ['boolean', 'select'],
          allowDefault: true,
          description: i18n.api.modal.configuration.magento.fields.backorders.description,
        },
      ],
    },

    // SEO & Visibility
    {
      title: i18n.api.modal.configuration.magento.sections.seoVisibility.title,
      description: i18n.api.modal.configuration.magento.sections.seoVisibility.description,
      fields: [
        {
          key: 'meta_title_attribute',
          label: i18n.api.modal.configuration.magento.fields.metaTitle.label,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
          description: i18n.api.modal.configuration.magento.fields.metaTitle.description,
        },
        {
          key: 'meta_description_attribute',
          label: i18n.api.modal.configuration.magento.fields.metaDescription.label,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
          description: i18n.api.modal.configuration.magento.fields.metaDescription.description,
        },
        {
          key: 'url_key_attribute',
          label: i18n.api.modal.configuration.magento.fields.urlKey.label,
          type: 'attribute',
          filterType: ['text'],
          allowNone: true,
          description: i18n.api.modal.configuration.magento.fields.urlKey.description,
        },
      ],
    },
  ];

  return <BaseConnectorMapping {...props} sections={sections} />;
}
